/**
 * AAA-19: a single fused full-screen post pass — radial vignette (MULTIPLY, so it
 * can only darken, never brighten/clip the void), low-amplitude animated film grain,
 * and a sub-pixel chromatic aberration that grows toward the corners. Cinematic
 * depth without volumetrics. Kept deliberately restrained per the grounded brief.
 *
 * MUST be followed by an OutputPass (wired in scene.ts) — appending a raw pass after
 * the terminal UnrealBloomPass makes us own final sRGB encoding; OutputPass restores
 * it and prevents the gamma shift that would re-open the M1.1 BUG-2 clipping.
 */
import * as THREE from 'three';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

/** Grade preset for {@link createPostFx}. */
export interface PostFxOptions {
  /**
   * Cinematic intro grade: stronger funereal vignette (0.44), heavy filmic grain
   * (0.07, eased on the CineClock), and a wider anamorphic R/B corner split (1.7).
   * Gameplay defaults are unchanged when omitted/false.
   */
  cinematic?: boolean;
}

export const createPostFx = (options: PostFxOptions = {}): ShaderPass => {
  const cinematic = options.cinematic ?? false;
  const pass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uTime: { value: 0 },
      uVignette: { value: cinematic ? 0.44 : 0.34 }, // strength of the corner darkening
      uGrain: { value: cinematic ? 0.07 : 0.035 }, // grain amplitude
      uAberration: { value: cinematic ? 1.7 : 1.1 }, // px of R/B split at the corners
      // WAVE 2a: triangular (TPDF) blue-noise dither to break 8-bit banding in the
      // near-black void the design system mandates. Imperceptible amplitude (~1/255).
      // OFF in the cinematic path (0.0) — the intro grade is frozen for VRT parity.
      uDither: { value: cinematic ? 0.0 : 1.0 / 255.0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform float uTime, uVignette, uGrain, uAberration, uDither;
      uniform vec2 uResolution;
      varying vec2 vUv;
      float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
      void main() {
        vec2 toC = vUv - 0.5;
        float r = length(toC);
        // chromatic aberration: split R/B outward, scaled by radius^2 (corners only)
        vec2 dir = toC * (r * r) * (uAberration / uResolution.x);
        vec3 col;
        col.r = texture2D(tDiffuse, vUv + dir).r;
        col.g = texture2D(tDiffuse, vUv).g;
        col.b = texture2D(tDiffuse, vUv - dir).b;
        // vignette: smooth radial multiply (darken only)
        float vig = smoothstep(0.85, 0.35, r);
        col *= mix(1.0, vig, uVignette);
        // animated grain, additive but tiny and centred on 0
        float g = (hash(vUv * uResolution.xy + uTime) - 0.5) * uGrain;
        col += g;
        // WAVE 2a: TPDF dither — difference of two uniform samples gives a triangular
        // distribution, the textbook anti-banding noise. Amplitude ~1/255, so it only
        // dislodges quantisation on the near-black gradients, never visible as grain.
        float d = hash(vUv * uResolution.xy + uTime + 17.0) - hash(vUv * uResolution.xy - uTime + 41.0);
        col += d * uDither;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  return pass;
};
